import { evalCondition, notifyParent } from 'mailup-react-helpers'
import {
  Actions,
  DataListHeader,
  DataListRow,
  Flex,
  Grid,
  GridCell,
  LINK_VARIANT_EXTRALIGHT,
  LINK_VARIANT_HIGHLIGHT,
  Link,
  Panel,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  TEXT_VARIANT_LIGHT,
  TEXT_VARIANT_SMALL_LIGHT,
  Text,
  TruncateAndTooltip,
} from 'mailup-web-components'
import React from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useRecoilCallback, useRecoilValue } from 'recoil'
import Pagination from '../Pagination'
import { laodTrendTableCallback } from '../recoil/callbacks/callbacks'
import {
  tableLoadingState,
  tableStateResult,
  tableStateSerach,
} from '../recoil/state/result'

const ACTION_WIDTH = '28px'

const TrendTable = () => {
  const intl = useIntl()
  const laodTrendTable = useRecoilCallback(laodTrendTableCallback, [])
  const { orderBy, orderDescending } = useRecoilValue(tableStateSerach)
  const results = useRecoilValue(tableStateResult)

  const loading = useRecoilValue(tableLoadingState)

  const hasStatPermission = evalCondition('uo:STAT')
  const { idRecipient } = useParams()

  const onClickOrder = (data) => {
    laodTrendTable({
      idRecipient,
      orderBy: data,
    })
  }

  function getActionItems(item) {
    const actions = []
    if (hasStatPermission) {
      actions.push({
        label: intl.formatMessage({ id: 'Load.Statistics.StatsMsg' }),
        onClick: () =>
          notifyParent('redirectMainFrame', [
            `statistics_app_redirector.aspx?r=/stats/sendouts/sending/mode/messages/0/${item.IdMessage}`,
          ]),
        className: 'test-actionMenu-StatsMsg',
      })

      item.ServiceType !== 1 &&
        actions.push({
          label: intl.formatMessage({ id: 'Load.Statistics.StatsSend' }),
          onClick: () =>
            notifyParent('redirectMainFrame', [
              `statistics_app_redirector.aspx?r=/stats/sendouts/list/mode/sendings/msg/${item.IdMessage}`,
            ]),
          className: 'test-actionMenu-StatsSend',
        })
    }
    return actions
  }

  function goToSendoutsLink(item) {
    notifyParent('redirectMainFrame', [
      `statistics_app_redirector.aspx?r=/stats/sendouts/link/mode/messages/0/${item.IdMessage}`,
    ])
  }

  const heads = [
    {
      label: intl.formatMessage({ id: 'Load.MailupCommon.Message' }),
      sortable: true,
      field: 'RecipientCntOrderbyIdMessage',
      span: hasStatPermission ? 7 : 8,
    },
    {
      label: intl.formatMessage({ id: 'Load.ActiveRecipientTrend.Opens' }),
      sortable: true,
      field: 'RecipientCntOrderbyAT',
      align: 'right',
      span: 2,
    },
    {
      label: intl.formatMessage({ id: 'Load.ActiveRecipientTrend.Clicks' }),
      sortable: true,
      field: 'RecipientCntOrderbyCT',
      align: 'right',
      span: 2,
    },
    undefined,
    hasStatPermission
      ? {
          label: intl.formatMessage({ id: 'Load.MailupCommon.Actions' }),
          sortable: false,
          align: 'right',
          span: 1,
        }
      : undefined,
  ].filter((o) => o !== undefined)

  const handleMessageClick = (id) => {
    notifyParent('openPreviewMainFrame', [id])
  }

  return (
    <Panel>
      <DataListHeader
        heads={heads}
        orderByStatus={orderBy}
        orderDescendingStatus={orderDescending}
        handleChangeColumnsOrderCallback={onClickOrder}
        afterWidth={ACTION_WIDTH}
      />

      <section>
        {results.items.map((item, idx) => (
          <DataListRow data-mu-test="item" key={idx}>
            <Grid cols={12}>
              <GridCell
                span={hasStatPermission ? 7 : 8}
                data-mu-test="item-message"
              >
                {loading || !item.Subject ? (
                  <>
                    <SkeletonLoader
                      variant={SKELETON_LOADER_VARIANT_ONWHITE}
                      style={{ marginBottom: '4.5px' }}
                    />
                    <SkeletonLoader
                      variant={SKELETON_LOADER_VARIANT_ONWHITE}
                      height="14px"
                      style={{ marginBottom: '4.2px' }}
                    />
                  </>
                ) : (
                  <>
                    <Link
                      variant={LINK_VARIANT_HIGHLIGHT}
                      data-mu-test="item-subject"
                      onClick={() => handleMessageClick(item.IdMessage)}
                    >
                      <TruncateAndTooltip text={item.Subject} />
                    </Link>
                    <Text variant={TEXT_VARIANT_SMALL_LIGHT} htmlElement="div">
                      <TruncateAndTooltip text={item.Note} />
                    </Text>
                  </>
                )}
              </GridCell>
              <GridCell span={2} data-mu-test="item-opens">
                {loading ? (
                  <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                ) : (
                  <Flex justifyContent="flex-end">
                    <Text variant={TEXT_VARIANT_LIGHT}>
                      {intl.formatNumber(item.TotalCounters.AT)}
                    </Text>
                  </Flex>
                )}
              </GridCell>
              <GridCell span={2} data-mu-test="item-clicks">
                {loading ? (
                  <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                ) : (
                  <Flex justifyContent="flex-end">
                    {hasStatPermission ? (
                      <Link
                        variant={LINK_VARIANT_EXTRALIGHT}
                        onClick={() => goToSendoutsLink(item)}
                      >
                        {intl.formatNumber(item.TotalCounters.CT)}
                      </Link>
                    ) : (
                      <Text variant={TEXT_VARIANT_LIGHT}>
                        {intl.formatNumber(item.TotalCounters.CT)}
                      </Text>
                    )}
                  </Flex>
                )}
              </GridCell>
              {hasStatPermission && (
                <GridCell span={1}>
                  <Flex justifyContent="flex-end">
                    <Actions
                      items={getActionItems(item)}
                      iconMenu="icon-action"
                      data-mu-test={
                        'item-actions item-actions_' + item.IdMessage
                      }
                      disabled={loading}
                    />
                  </Flex>
                </GridCell>
              )}
            </Grid>
          </DataListRow>
        ))}
      </section>

      <Pagination />
    </Panel>
  )
}

export default TrendTable
